/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useConfig } from "../lib/react-query/useConfig";
import { useProductsTypeProduct } from "../lib/react-query/useProducts";
import { useGetMemberships } from "../lib/react-query/useGetMemberships";
import { useGetPromotions } from "../lib/react-query/useGetPromotions";
import { usePackages } from "../lib/react-query/usePackages";
import usePluginUtils from "./usePluginUtils";

export const useCustomFeatures = () => {
  const { isPluginEnabled } = usePluginUtils();
  const { data: config } = useConfig();
  const isProductsMenuEnabled = isPluginEnabled("product") && !!config?.product_use_menu;
  const isMembershipEnabled = isPluginEnabled("membership");
  const isPromotionsEnabled = isPluginEnabled("promotion");
  const isPackagesEnabled = isPluginEnabled("package");

  const { data: productsData, isLoading: isProductsLoading } = useProductsTypeProduct({
    standaloneOnly: true,
    enabled: isProductsMenuEnabled,
  });

  const { data: membershipsData, isLoading: isMembershipsLoading } = useGetMemberships(
    {},
    { enabled: isMembershipEnabled }
  );

  const { data: promotionsData, isLoading: isPromotionsLoading } = useGetPromotions(
    {},
    { enabled: isPromotionsEnabled }
  );

  const { data: packagesData, isLoading: isPackagesLoading } = usePackages({
    enabled: isPackagesEnabled,
  });

  const hasProductsForSale =
    isProductsMenuEnabled &&
    (productsData?.pages?.flatMap(p => p)?.filter(p => !p.services?.length) ?? []).length > 0;

  const hasGiftCards = isPromotionsEnabled && (promotionsData?.data?.length ?? 0) > 0;

  const hasPackages =
    isPackagesEnabled &&
    Array.isArray(packagesData) &&
    packagesData.length > 0;

  const hasMemberships =
    isMembershipEnabled && (membershipsData?.data?.length ?? 0) > 0;

  return {
    hasProductsForSale,
    hasGiftCards,
    hasMemberships,
    hasPackages,
    showCart: hasProductsForSale || hasGiftCards || hasMemberships || hasPackages,
    isLoading:
      (isProductsMenuEnabled && isProductsLoading) ||
      (isPromotionsEnabled && isPromotionsLoading) ||
      (isMembershipEnabled && isMembershipsLoading) ||
      (isPackagesEnabled && isPackagesLoading),
  };
};